import { useEffect, useState } from "react";
import { Search, Hash, ArrowLeft } from "lucide-react";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import CitycollabNavigationRoutes from "../utils/navigationRoutes";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ZulipServices from "../services/APIs/zulipServices";
import { Button } from "../components/ui/button";

import { useChatDMChannelList } from "../hooks/useChatDMChannelList";

// Helper function to format time ago
const formatTimeAgo = (timestamp: number) => {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60);
  const hours = Math.floor(diff / 3600);
  const days = Math.floor(diff / 86400);

  if (minutes < 1) {
    return "Just now";
  }
  if (minutes < 60) {
    return `${minutes} min ago`;
  }
  if (hours < 24) {
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  }
  if (days < 7) {
    return `${days} day${days > 1 ? "s" : ""} ago`;
  }
  return new Date(timestamp * 1000).toLocaleDateString();
};

const Messages = () => {
  const navigate = useNavigate();
  const { channelList, dmUserList, loading: isLoading } = useChatDMChannelList();
  const isLoadingDMs = isLoading;
  const isLoadingChannels = isLoading;

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8">
      <header className="sticky top-0 z-50 border-b border-border bg-card shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(CitycollabNavigationRoutes.NEW_DESIGN)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-primary md:text-xl">
                Messages
              </h1>
              <p className="text-xs text-muted-foreground">Stay connected</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 md:py-8">
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search messages..." className="pl-10" />
          </div>
        </div>

        <Tabs defaultValue="direct" className="w-full">
          <TabsList className="mb-6 grid w-full grid-cols-2">
            <TabsTrigger value="direct">Direct Messages</TabsTrigger>
            <TabsTrigger value="channels">Channels</TabsTrigger>
          </TabsList>

          <TabsContent value="direct" className="space-y-2">
            {isLoadingDMs ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Loading messages...
              </div>
            ) : dmUserList.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No direct messages yet
              </div>
            ) : (
              dmUserList.map(chat => (
                <Card
                  key={chat.id}
                  className="cursor-pointer p-4 transition-colors hover:bg-muted/50"
                  onClick={() =>
                    navigate(
                      `/conversations/${chat.id}?dmId=${chat.id}`
                    )
                  }
                >
                  <div className="flex items-start gap-3">
                    {chat.avatar_url ? (
                      <img
                        src={chat.avatar_url}
                        alt={chat.full_name}
                        className="h-10 w-10 shrink-0 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary font-medium text-primary-foreground">
                        {chat.full_name
                          ?.split(" ")
                          .map((n: string) => n[0])
                          .join("")
                          .slice(0, 2)
                          .toUpperCase() ?? "U"}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex items-center justify-between">
                        <h3 className="truncate font-medium">
                          {chat.full_name ?? "Unknown User"}
                        </h3>
                        <span className="ml-2 shrink-0 text-xs text-muted-foreground">
                          {formatTimeAgo(chat.timestamp)}
                        </span>
                      </div>
                      <p className="truncate text-sm text-muted-foreground">
                        {chat.last_message ?? "No messages"}
                      </p>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="channels" className="space-y-2">
            {isLoadingChannels ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Loading channels...
              </div>
            ) : channelList.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No channels available
              </div>
            ) : (
              channelList.map((channel: any) => (
                <Card
                  key={channel.stream_id ?? channel.id}
                  className="cursor-pointer p-4 transition-colors hover:bg-muted/50"
                  onClick={() =>
                    navigate(
                      `/channels/${channel.stream_id ?? channel.id}?channelId=${channel.stream_id ?? channel.id}`
                    )
                  }
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
                      <Hash className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex items-center justify-between">
                        <h3 className="font-medium">
                          #{channel.name ?? "Unknown Channel"}
                        </h3>
                      </div>
                      {channel.description && (
                        <p className="truncate text-sm text-muted-foreground">
                          {channel.description}
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Messages;
